import { endOfMonth, formatISO, startOfMonth, subMonths } from "date-fns";
import { TaskCreate } from "@common/contracts/task.contract";
import {
    WORKSPACE_WORKFLOW_TASK_TYPES,
    getStickyMembershipKind,
    getTaskTypeLabel,
} from "@common/constants/taskTypeDefinitions";
import { TaskType } from "@common/types/task";
import { PersistableView, ViewType } from "@common/types/views/views";
import {
    isReportTaskType,
    replaceReportDisplayFilters,
    type ReportTaskType,
} from "@components/reports/reportDisplayFilters";
import { getTaskPreviewDefinition } from "./taskCreationPreviewRegistry";

export type PeriodTaskType = ReportTaskType | TaskType.DOCUMENT_RETRIEVAL;

export const getLockedWorkflowOption = (type: TaskType) => ({
    value: type,
    label: getTaskTypeLabel(type) ?? type,
});

type TaskViewScopeValues = Pick<TaskCreate, "type" | "view" | "entityIds">;

export const withTaskViewEntities = ({
    type,
    view,
    entityIds,
}: TaskViewScopeValues): PersistableView | undefined => {
    const scopeViewToEntities =
        getTaskPreviewDefinition(type)?.scopeViewToEntities;
    return view && scopeViewToEntities
        ? scopeViewToEntities(view, entityIds)
        : view;
};

export const getSelectionTaskView = (
    values: TaskViewScopeValues,
): PersistableView | undefined =>
    getTaskPreviewDefinition(values.type)?.selectionViewOwnsEntities
        ? values.view
        : withTaskViewEntities(values);

export const isWorkspaceWorkflowType = (type: TaskType): boolean =>
    WORKSPACE_WORKFLOW_TASK_TYPES.includes(type);

export const isPeriodTaskType = (type: TaskType): type is PeriodTaskType =>
    type === TaskType.PROFIT_LOSS ||
    type === TaskType.BALANCE_SHEET ||
    type === TaskType.DOCUMENT_RETRIEVAL;

const VIEW_TYPE_BY_TASK_TYPE: Record<PeriodTaskType, ViewType> = {
    [TaskType.PROFIT_LOSS]: ViewType.PROFIT_LOSS,
    [TaskType.BALANCE_SHEET]: ViewType.BALANCE_SHEET,
    [TaskType.DOCUMENT_RETRIEVAL]: ViewType.FINANCIAL_DOCUMENT,
};

interface WorkflowPeriod {
    periodStart: string;
    periodEnd: string;
}

export const getDefaultWorkflowPeriod = ({
    now,
}: {
    now: Date;
}): WorkflowPeriod => {
    const previousMonth = subMonths(now, 1);
    return {
        periodStart: formatISO(startOfMonth(previousMonth), {
            representation: "date",
        }),
        periodEnd: formatISO(endOfMonth(previousMonth), {
            representation: "date",
        }),
    };
};

export const buildWorkflowView = ({
    taskType,
    periodStart,
    periodEnd,
}: WorkflowPeriod & { taskType: PeriodTaskType }): PersistableView => ({
    type: VIEW_TYPE_BY_TASK_TYPE[taskType],
    columns: [],
    filters: { periodStart, periodEnd },
});

export const getInitialWorkflowView = ({
    taskType,
    now,
}: {
    taskType: TaskType;
    now: Date;
}): PersistableView | undefined =>
    isPeriodTaskType(taskType)
        ? buildWorkflowView({
              taskType,
              ...getDefaultWorkflowPeriod({ now }),
          })
        : undefined;

export const getWorkflowViewForType = ({
    type,
    currentView,
    now,
}: {
    type: TaskType;
    currentView: TaskCreate["view"];
    now: Date;
}): PersistableView | undefined => {
    const createDefaultView = getTaskPreviewDefinition(type)?.createDefaultView;
    if (createDefaultView) {
        return createDefaultView();
    }
    if (!isPeriodTaskType(type)) {
        return undefined;
    }
    const defaultPeriod = getDefaultWorkflowPeriod({ now });
    const view = buildWorkflowView({
        taskType: type,
        periodStart:
            currentView?.filters?.periodStart ?? defaultPeriod.periodStart,
        periodEnd: currentView?.filters?.periodEnd ?? defaultPeriod.periodEnd,
    });
    if (!isReportTaskType(type)) {
        return view;
    }
    return {
        ...view,
        filters: replaceReportDisplayFilters({
            taskType: type,
            filters: view.filters,
            displayFilters: currentView?.filters,
        }),
    };
};

interface WorkflowAccount {
    id: number;
    entityId?: number | null;
}

interface WorkflowBlockerParams extends Pick<
    TaskCreate,
    "type" | "entityIds" | "view" | "sticky"
> {
    accounts: WorkflowAccount[];
}

export const getWorkflowTaskBlocker = ({
    type,
    entityIds,
    view,
    sticky,
    accounts,
}: WorkflowBlockerParams): string | null => {
    // A sticky task resolves its membership per task entity; transactions
    // resolve theirs across the workspace.
    const membershipKind = getStickyMembershipKind(type);
    if (
        sticky &&
        membershipKind !== null &&
        membershipKind !== "transactions" &&
        !entityIds?.length
    ) {
        return "Select an entity for this task";
    }
    if (!isPeriodTaskType(type)) {
        return null;
    }
    if (entityIds?.length !== 1) {
        return "Select one entity for this task";
    }
    const periodStart = view?.filters?.periodStart;
    const periodEnd = view?.filters?.periodEnd;
    if (!periodStart || !periodEnd) {
        return "Select a start and end month";
    }
    if (periodStart > periodEnd) {
        return "End month must be on or after start month";
    }
    if (type !== TaskType.DOCUMENT_RETRIEVAL) {
        return null;
    }
    const accountId = view?.filters?.accountIds?.[0];
    const isAccountOfEntity = accounts.some(
        (account) =>
            account.id === accountId && account.entityId === entityIds[0],
    );
    if (!isAccountOfEntity) {
        return "Select a financial account";
    }
    if (!view?.filters?.documentCategoryIds?.length) {
        return "Select a document category";
    }
    return null;
};

interface TaskCreationContext {
    viewConfig?: object;
    transactionsTask?: object;
    perEntityTask?: object;
    customOnSubmit?: object;
    relatedTaskId?: string;
}

export const hasTaskCreationContext = ({
    viewConfig,
    transactionsTask,
    perEntityTask,
    customOnSubmit,
    relatedTaskId,
}: TaskCreationContext): boolean =>
    !!viewConfig ||
    !!transactionsTask ||
    !!perEntityTask ||
    !!customOnSubmit ||
    !!relatedTaskId;

export const canShowWorkflowPicker = ({
    taskType,
    hasContext,
}: {
    taskType: TaskType;
    hasContext: boolean;
}): boolean => isWorkspaceWorkflowType(taskType) && !hasContext;

export const getCreateTaskBlocker = ({
    title,
    ...workflowParams
}: WorkflowBlockerParams & { title: string }): string | null =>
    getWorkflowTaskBlocker(workflowParams) ??
    (title.trim() ? null : "Title is required");

export enum TaskSplit {
    ENTITY = "entity",
    TRANSACTION = "transaction",
}

export const getTaskSplit = ({
    createTaskPerTransaction,
}: Pick<TaskCreate, "createTaskPerTransaction">): TaskSplit =>
    createTaskPerTransaction ? TaskSplit.TRANSACTION : TaskSplit.ENTITY;
